import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import Card from "../shared/Card/Card";
import ContactFromId from "../contacts/ContactFromId";
import LoadingWheel from "../shared/LoadingWheel";
import formatDateTime from "../../utils/formatDateTime";
import { PartStatus } from "../../models/Part";
import usePartLocationHistory, {
  IPartLocationHistoryEntry,
  PartLocationChangeType,
} from "../../data/usePartLocationHistory";

const changeLabel = (changeType: PartLocationChangeType) => {
  switch (changeType) {
    case PartLocationChangeType.Placed:
      return "Placed";
    case PartLocationChangeType.Moved:
      return "Moved";
    case PartLocationChangeType.Removed:
      return "Removed";
    default:
      return "Changed";
  }
};

const changeColour = (changeType: PartLocationChangeType) => {
  switch (changeType) {
    case PartLocationChangeType.Placed:
      return "bg-green-100 text-green-700";
    case PartLocationChangeType.Moved:
      return "bg-blue-100 text-blue-700";
    case PartLocationChangeType.Removed:
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

/**
 * A location that has since been deleted comes back with no name. Show the id anyway - it's still
 * something the warehouse can search on, and blanking it would hide that the part was ever there.
 */
const locationName = (name: string | null, id: string | null, t: (key: string) => string) => {
  if (name) return name;
  if (id) return `${t("Deleted location")} (${id.slice(0, 8)})`;
  return "—";
};

export default function PartLocationHistoryCard({ partId }: { partId: string }) {
  const { t } = useTranslation("stock");
  const { partLocationHistory } = usePartLocationHistory(partId, "PartLocationHistoryCard");

  const history = partLocationHistory.data?.data;
  const entries: IPartLocationHistoryEntry[] = history?.entries ?? [];

  return (
    <Card
      title={t("Location History")}
      bodyClassName="p-4"
      collapsible
      defaultOpen={false}
      headerRight={
        entries.length > 0 ? (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {entries.length}
          </span>
        ) : undefined
      }
    >
      {partLocationHistory.isLoading ? (
        <LoadingWheel />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-700">
            {history?.currentLocationName ? (
              <>
                {t("Currently")}: <span className="font-medium">{history.currentLocationName}</span>
              </>
            ) : history?.lastKnownLocationName ? (
              <span className="italic text-gray-500">
                {t("No location")} — {t("last seen")}: {history.lastKnownLocationName}
              </span>
            ) : (
              <span className="italic text-gray-500">{t("No location set")}</span>
            )}
          </p>

          {entries.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t("No location changes recorded for this part yet. Moves are recorded from the point this was switched on, so older moves won't appear here.")}
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-300">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border-b border-gray-400 px-3 py-2 text-left text-xs font-medium text-gray-700">
                        {t("When")}
                      </th>
                      <th className="border-b border-gray-400 px-3 py-2 text-left text-xs font-medium text-gray-700">
                        {t("Change")}
                      </th>
                      <th className="border-b border-gray-400 px-3 py-2 text-left text-xs font-medium text-gray-700">
                        {t("From")}
                      </th>
                      <th className="border-b border-gray-400 px-3 py-2 text-left text-xs font-medium text-gray-700">
                        {t("To")}
                      </th>
                      <th className="border-b border-gray-400 px-3 py-2 text-left text-xs font-medium text-gray-700">
                        {t("By")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {entries.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-b border-gray-200 transition odd:bg-white even:bg-gray-50 hover:bg-gray-100"
                      >
                        <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                          {formatDateTime(entry.changedOn, DateTime.DATETIME_SHORT)}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${changeColour(entry.changeType)}`}
                          >
                            {t(changeLabel(entry.changeType))}
                          </span>
                          {/* The status is what explains a location being cleared - a return, a despatch. */}
                          <p className="mt-1 text-xs text-gray-500">{t(PartStatus[entry.partStatus])}</p>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          {locationName(entry.fromLocationName, entry.fromWarehouseLocationId, t)}
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          {locationName(entry.toLocationName, entry.toWarehouseLocationId, t)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          {entry.userId ? <ContactFromId contactId={entry.userId} /> : t("System")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
